import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import axios, { AxiosInstance } from "axios";
import config from "../config";
import { authService } from "./authService";

const api = new APIClient();
const { api: apiConfig } = config;

// Create a separate axios instance for blob downloads (bypasses interceptors)
const blobAxios: AxiosInstance = axios.create({
  baseURL: apiConfig.API_URL,
  responseType: 'blob',
});

// Add request interceptor to attach auth token for blob downloads
blobAxios.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Defined locally to avoid circular dependency, should match AddItemModal definition
export interface ProductLineItem {
    id: string; 
    productId: string;
    productName: string;
    quantity: number;
    rate: number;
}

/**
 * Labour Calculation Type Enum (API values)
 * Matches backend enum exactly
 */
export enum LabourCalculationType {
    Fixed = 1,
    PerHour = 2,
    PerDay = 3,
    VolumeBased = 4,
    RoleBased = 5
}

/**
 * Helper function to check if labourCalculationType is VolumeBased
 * Handles both enum number (4) and string ('VolumeBased') for backward compatibility
 */
export function isVolumeBasedLabour(calculationType: string | number | undefined): boolean {
    if (calculationType === undefined || calculationType === null) return false;
    return calculationType === LabourCalculationType.VolumeBased || 
           calculationType === 'VolumeBased' || 
           calculationType === 4;
}

// UI Interface
export interface QuoteItem {
  itemType: number; // 1=Rental (Legacy/Removed), 2=OneOff (Labour), 3=Cartage/Travel, 4=Custom, 5=Structure (UI Only)
  scaffoldTaskType?: number; // Quote module: 1=Erect, 3=Dismantle only
  taskDescription: string;
  quantity?: number;
  finalUnitPrice?: number;
  
  // IDs for Edit operations
  id?: string; // Item ID from API
  quoteJobId?: string; // Job ID this item belongs to
  
  // Custom / Other
  customItemCategory?: number; // 0=General, 1=ExternalInspection, 2=PrimeCost, 3=Other
  displayRentalRateType?: number; // 1=FixedAmount, 2=PerHour, 3=PerDay, 4=PerWeek, 5=PerVisit
  genericRate?: number;
  weeklyRate?: number;
  isRecurring?: boolean;
  recurrencePattern?: number; // 1=Weekly, 2=Monthly
  
  // Rental
  isPriceDerivedFromProduct?: boolean;
  rentalRate?: number;

  // Cartage / Travel
  cartageVehicleType?: number; // 1=Ute (Travel), 3=Truck (Cartage)
  distanceKM?: number;
  cartageRate?: number;

  // UI Only - Preserved for Editing
  uiPricingMethod?: string; // Fixed, ProductBase, Dynamic, FixedVolume
  labourHours?: number;
  labourRate?: number;
  roleId?: string; // Stored to re-select role
  structureLineItems?: ProductLineItem[]; // Store lines for Structure
  structureFixedPrice?: number; // Store base fixed price for Structure
  labourLineItems?: LabourLineItem[]; // Store roles for Labour (similar to structureLineItems)
  
  // Volume-Based Pricing (UI Only - for Structure Fixed+Volume and Labour VolumeBased)
  length?: number; // Dimensions in meters
  depth?: number; // Dimensions in meters
  height?: number; // Dimensions in meters
  volumeBasedLabourRate?: number; // Rate per m³ (for Labour) or Variable Rate per m³ (for Structure Fixed+Volume)
  fixedMobilisationCharge?: number; // Fixed charge for Labour
  extraAccessCost?: number; // Extra access cost for Labour
  labourCalculationType?: 'Fixed' | 'PerHour' | 'PerDay' | 'VolumeBased' | 'RoleBased'; // Labour calculation type
}

// UI Interface for Labour roles (similar to ProductLineItem)
export interface LabourLineItem {
  id: string;
  roleId: string;
  roleName: string;
  quantity: number;
  hours: number;
  ratePerHour: number;
    scaffoldTaskType: number; // Quote module: 1=Erect, 3=Dismantle only (Modify=2 not used in Quote)
}

// UI-only Volume State (not sent to backend)
export type VolumeSource = 'StructureErect' | 'StructureDismantle' | 'Labour' | null;

export interface QuoteJobVolumeState {
  length: number;
  depth: number;
  height: number;
  source: VolumeSource;
}

// UI Interface
export interface QuoteGroup {
  name: string;
  description?: string;
  sequence: number;
  rentalCalculationType: number; // 1=Fixed, 2=% of Erect, 3=% of Dismantle, 4=% of Total
  rentalConfigValue: number;
  rentalFrequency: number; // 1=Daily, 2=Weekly, 3=Monthly
  complianceCalculationType: number; // 1=Fixed, 2=% of Erect, 3=% of Dismantle, 4=% of Total
  complianceConfigValue: number;
  complianceFrequency: number; // 1=Daily, 2=Weekly, 3=Monthly
  items: QuoteItem[];
  id?: string; // Job ID from API for Edit operations
  // UI-only volume state (not sent to backend)
  volumeState?: QuoteJobVolumeState;
}

// API Interfaces
export interface QuoteItemDetailsPayload {
    id?: string; // Item detail ID for Edit operations
    productId: string;
    quantity: number;
    unitPrice?: number;
    notes?: string;
    sequence?: number;
}

export interface QuoteItemLabourRatePayload {
    id?: string; // Labour rate ID for Edit operations
    roleId: string;
    staffCount: number;
    hoursPerStaff: number;
    ratePerHour: number;
    notes?: string;
    sequence?: number;
}

export interface QuoteItemPayload {
    id?: string; // Item ID for Edit operations
    quoteJobId?: string; // Job ID this item belongs to (for Edit operations)
    itemType: number; // 1=Labour, 2=Structure, 3=Logistics, 4=Custom (API)
    workItemType?: number; // Sent on POST/PUT. Backend uses this; 1=Labour, 2=Structure, 3=Logistics, 4=Custom
    taskDescription: string;
    quantity?: number;
    unitPrice?: number;
    sequence?: number;

    // Labour, Structure, Logistics - Task Phase (Quote module: Erect=1, Dismantle=3 only)
    scaffoldTaskType?: number; // Quote module: 1=Erect, 3=Dismantle only (Modify=2 not used in Quote)

    // Labour - New structure with quoteItemLabourRates array
    quoteItemLabourRates?: QuoteItemLabourRatePayload[];
    
    // Labour - Volume-Based fields
    labourCalculationType?: number; // Enum: 1=Fixed, 2=PerHour, 3=PerDay, 4=VolumeBased, 5=RoleBased
    labourRate?: number; // Rate per m³ for VolumeBased
    fixedMobilisationCharge?: number; // Fixed charge for VolumeBased

    // Structure
    structurePricingMethod?: number; // 1=Fixed, 2=Product, 3=Hybrid, 4=Fixed+Volume
    fixedBaseAmount?: number;
    quoteItemDetails?: QuoteItemDetailsPayload[];
    
    // Structure - Fixed + Volume fields
    variableRate?: number; // Variable rate per m³ for Fixed+Volume
    
    // Shared dimensions for Labour VolumeBased and Structure Fixed+Volume
    lengthMeters?: number; // Dimensions in meters
    depthMeters?: number; // Dimensions in meters
    heightMeters?: number; // Dimensions in meters

    // Logistics
    cartageVehicleType?: number;
    distanceKM?: number;
    cartageRate?: number;

    // Custom
    customItemCategory?: number;
    isRecurring?: boolean;
    billingFrequency?: number;
    customRateType?: number;
    customRateAmount?: number;
}

export interface QuoteJobPayload {
    id?: string; // Job ID for Edit operations
    jobName: string;
    jobDescription?: string;
    sequence: number;
    rentalCalculationType: number;
    rentalConfigValue: number;
    rentalFrequency: number;
    complianceCalculationType: number;
    complianceConfigValue: number;
    complianceFrequency: number;
    items: QuoteItemPayload[];
}

export interface QuotePayload {
  tenantId?: string;
  leadId?: string | null;
  clientId?: string | null;
  siteId?: string | null;
  quoteTitle: string;
  jobDescription: string;
  quoteDate: string;
  validForDays?: number;
  paymentTerms?: string;
  notes?: string;
  assignedSupervisorId?: string | null;
  quoteNumber?: string; // Job Quote Number (auto-generated or manual)
  jobNumber?: string; // Job Reference Number (auto-generated or from selected job)
  jobId?: string | null; // Selected Job ID (optional, UUID)
  quoteJobs: QuoteJobPayload[];
}

export const createQuote = (data: QuotePayload) => {
  console.log("quoteService.createQuote called with:", data);
  return api.create("/Quote", data);
};

export const markQuoteAsWon = (id: string) => {
  return api.create(`/Quote/${id}/MarkAsWon`, {});
};

export const convertLeadToClient = (leadId: string) => {
  return api.create("/Lead/ConvertToClient", { leadId });
};

export const convertQuoteToWorkOrder = (
  quoteId: string,
  siteId: string,
  workOrderTitle: string,
  startDate: string,
  createSeparateWorkOrdersPerQuoteJob: boolean = false
) => {
  // API expects PascalCase field names matching AwardQuoteRequest class
  return api.create(`/Quote/${quoteId}/AwardToWorkOrder`, {
    SiteId: siteId,
    WorkOrderTitle: workOrderTitle,
    StartDate: startDate,
    CreateSeparateWorkOrdersPerQuoteJob: createSeparateWorkOrdersPerQuoteJob,
  });
};

export const revertAwardQuoteToWo = (quoteId: string): Promise<QuoteListItem> => {
  console.log("quoteService.revertAwardQuoteToWo called with id:", quoteId);
  return api.create<QuoteListItem>(`/Quote/${quoteId}/RevertAwardQuoteToWo`, {});
};

// Quote List Interfaces
// WORKFLOW: Import workflow types from central location
import { QuoteStatus, QuoteWorkflowMetadata } from '../types/quotes';

export interface QuoteListItem extends QuoteWorkflowMetadata {
  id: string;
  tenantId: string;
  tenantName?: string;
  quoteNumber?: string;
  jobNumber?: string; // Job Reference Number
  jobId?: string | null; // Selected Job ID
  leadId?: string | null;
  clientId?: string | null;
  siteId?: string | null;
  quoteTitle?: string;
  jobDescription: string;
  quoteDate: string;
  validUntil?: string; // API uses validUntil instead of expiryDate
  validForDays?: number;
  expiryDate?: string; // For backward compatibility
  paymentTerms?: string;
  notes?: string;
  assignedSupervisorId?: string | null;
  assignedSupervisorName?: string;
  /**
   * Quote status (workflow state)
   * Uses QuoteStatus enum values: 1=Draft, 2=UnderReview, 3=ChangeRequested, 4=Approved, 5=Sent, 6=Awarded, 7=Rejected, 8=Expired
   * Legacy values (0=Draft, 1=Sent, etc.) are deprecated but may still exist in old data
   */
  status?: QuoteStatus | number; // Allow number for backward compatibility with legacy status values
  subTotal?: number;
  gst?: number;
  total?: number; // API uses total instead of totalAmount
  totalAmount?: number; // For backward compatibility
  leadName?: string;
  clientName?: string;
  siteName?: string;
  leadSiteAddress?: string;
  convertedFromLeadId?: string | null;
  convertedFromLeadDate?: string;
  createdByUserId?: string;
  createdByUserName?: string;
  lastFollowUpDate?: string;
  nextFollowUpDate?: string;
  isDeleted?: boolean;
  created?: string;
  modified?: string;
  quoteJobs?: QuoteJobResponse[]; // Included when includeItems=true
}

// API Response Interfaces (for GetById with includeItems=true)
export interface QuoteItemDetailResponse {
  id: string;
  quoteItemId: string;
  productId: string;
  productName: string;
  productCode: string;
  quantity: number;
  unitRentalRate?: number;
  unitPrice?: number;
  totalPrice?: number;
  notes?: string;
  sequence?: number;
  isDeleted?: boolean;
}

export interface QuoteItemLabourRateResponse {
  id: string;
  quoteItemId: string;
  roleId: string;
  roleName?: string; // May be populated by API
  staffCount: number;
  hoursPerStaff: number;
  ratePerHour: number;
  notes?: string;
  sequence?: number;
  isDeleted?: boolean;
}

export interface QuoteItemResponse {
  id: string;
  quoteId: string;
  quoteJobId: string;
  itemType: number; // 1=Labour, 2=Structure, 3=Logistics, 4=Custom (API)
  workItemType?: number; // Returned by GET; prefer for display. 1=Labour, 2=Structure, 3=Logistics, 4=Custom
  taskDescription: string;
  quantity?: number;
  sequence?: number;
  scaffoldTaskType?: number; // Quote module: 1=Erect, 3=Dismantle only
  
  // Labour - quoteItemLabourRates array
  quoteItemLabourRates?: QuoteItemLabourRateResponse[];
  
  // Labour - Volume-Based fields (may be present on API response)
  labourCalculationType?: number; // Enum: 1=Fixed, 2=PerHour, 3=PerDay, 4=VolumeBased, 5=RoleBased
  labourRate?: number; // Rate per m³ for VolumeBased
  fixedMobilisationCharge?: number; // Fixed charge for VolumeBased
  
  // Structure
  structurePricingMethod?: number; // 1=Fixed, 2=Product, 3=Hybrid, 4=Fixed+Volume
  fixedBaseAmount?: number;
  productCalculatedPrice?: number;
  quoteItemDetails?: QuoteItemDetailResponse[];
  
  // Structure - Fixed + Volume fields (may be present on API response)
  variableRate?: number; // Variable rate per m³ for Fixed+Volume
  
  // Shared dimensions for Labour VolumeBased and Structure Fixed+Volume (may be present on API response)
  lengthMeters?: number; // Dimensions in meters
  depthMeters?: number; // Dimensions in meters
  heightMeters?: number; // Dimensions in meters
  // Alternative field names (for backward compatibility)
  length?: number;
  depth?: number;
  height?: number;
  
  // Logistics
  cartageVehicleType?: number;
  distanceKM?: number;
  cartageRate?: number;
  
  // Custom
  customRateType?: number;
  customItemCategory?: number;
  customRateAmount?: number;
  isRecurring?: boolean;
  billingFrequency?: number;
  
  // Common
  calculatedPrice?: number;
  unitPrice?: number;
  effectivePrice?: number;
  totalPrice?: number;
  isDeleted?: boolean;
}

export interface QuoteJobResponse {
  id: string;
  jobName: string;
  jobDescription?: string;
  sequence: number;
  rentalCalculationType: number;
  rentalConfigValue: number;
  rentalFrequency: number;
  complianceCalculationType: number;
  complianceConfigValue: number;
  complianceFrequency: number;
  items: QuoteItemResponse[];
  totalErectPrice?: number;
  totalDismantlePrice?: number;
  estimatedRentalPrice?: number;
}

export interface QuotesResponse {
  items: QuoteListItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Service Functions
export const getAllQuotes = (
  pageNumber: number = 1,
  pageSize: number = 50
): Promise<QuotesResponse> => {
  return api.get<QuotesResponse>(url.GET_QUOTES, {
    pageNumber,
    pageSize,
  });
};

export const getQuoteById = (id: string, includeItems: boolean = true, includeItemDetails: boolean = true): Promise<QuoteListItem> => {
  return api.get<QuoteListItem>(`${url.GET_QUOTE_BY_ID}/${id}`, {
    includeItems: includeItems,
    includeItemDetails: includeItemDetails
  });
};

/**
 * Resolve the effective cartage rate-per-km for a given vehicle type.
 *
 * Hits the single backend endpoint `GET /TenantServiceCartageRate/Effective`,
 * which internally cascades Client rate (if a clientId is supplied and a client
 * override exists) → Tenant default rate. One call, no client/tenant fallback
 * needed on the FE.
 *
 * NOTE: the backend route returns `distance * ratePerKilometer` (and 0 when no
 * distance is passed), so we pass `distance = 1` to recover the bare per-km
 * rate. Returns 0 when no matrix rate exists for that vehicle.
 *
 * @param vehicleType - CommonEnums.CartageVehicleType int (1=Ute, 3=SmallTruck, …)
 * @param clientId - optional quote clientId; null/empty for a Lead quote → tenant-only
 * @param date - effective date (defaults to now)
 */
export const getEffectiveCartageRate = (
  vehicleType: number,
  clientId?: string | null,
  date?: Date
): Promise<number> => {
  const params: Record<string, string | number | boolean | undefined> = {
    vehicleType,
    distance: 1, // recover the per-km rate (endpoint returns distance * rate)
  };
  if (clientId) params.clientId = clientId;
  params.date = (date ?? new Date()).toISOString();
  return api.get<number>('/TenantServiceCartageRate/Effective', params);
};

export const updateQuote = (id: string, data: QuotePayload): Promise<QuoteListItem> => {
  // Match the exact same structure as createQuote (POST) - send QuotePayload directly
  // POST works with: api.create("/Quote", data) where data is QuotePayload
  // So PUT should also send QuotePayload directly, not wrapped
  console.log("📤 updateQuote - Payload (same structure as POST):", JSON.stringify(data, null, 2));
  console.log("📤 updateQuote - URL:", `${url.UPDATE_QUOTE}/${id}`);
  
  // Deep clone to ensure it's a plain object without any React/Redux references
  const serializedPayload = JSON.parse(JSON.stringify(data));
  
  // Send the same structure as POST - QuotePayload directly
  return api.put<QuoteListItem>(`${url.UPDATE_QUOTE}/${id}`, serializedPayload);
};

export const deleteQuote = (id: string): Promise<void> => {
  return api.delete<void>(`${url.DELETE_QUOTE}/${id}`);
};

export const sendQuoteForReview = (id: string): Promise<QuoteListItem> => {
  console.log("quoteService.sendQuoteForReview called with id:", id);
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/SendForReview`, {});
};

export const approveQuote = (id: string, reviewerRemarks: string): Promise<QuoteListItem> => {
  console.log("quoteService.approveQuote called with id:", id, "remarks:", reviewerRemarks);
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/Approve`, { reviewerRemarks });
};

export const rejectQuote = (id: string, reviewerRemarks: string): Promise<QuoteListItem> => {
  console.log("quoteService.rejectQuote called with id:", id, "remarks:", reviewerRemarks);
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/Reject`, { reviewerRemarks });
};

export const rejectQuoteToDraft = (id: string, reviewerRemarks: string): Promise<QuoteListItem> => {
  console.log("quoteService.rejectQuoteToDraft called with id:", id, "remarks:", reviewerRemarks);
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/RejectToDraft`, { reviewerRemarks });
};

export const sendQuoteToClient = (id: string): Promise<QuoteListItem> => {
  console.log("quoteService.sendQuoteToClient called with id:", id);
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/Send`, {});
};

export const markQuoteAsAwarded = (id: string): Promise<QuoteListItem> => {
  console.log("quoteService.markQuoteAsAwarded called with id:", id);
  // Using MarkAsWon endpoint which already exists
  return api.create<QuoteListItem>(`${url.EXECUTE_QUOTE_ACTION}/${id}/MarkAsWon`, {});
};

export const autogenerateQuotationCode = (): Promise<{ isSuccess: boolean; message: string; generatedCode: string }> => {
  console.log("quoteService.autogenerateQuotationCode called");
  return api.create<{ isSuccess: boolean; message: string; generatedCode: string }>("/Quote/AutogenerateQuotationCode", {});
};

// Generate PDF for a quote
export const generateQuotePdf = async (quoteId: string): Promise<Blob> => {
  // Use separate axios instance for blob downloads to bypass response interceptors
  // This ensures the binary data is not corrupted by response transformation
  // The blobAxios instance has its own request interceptor to add auth token
  const response = await blobAxios.get(`/Quote/${quoteId}/GeneratePdf`, {
    headers: {
      Accept: 'application/pdf',
    },
  });
  
  // Response.data should be the blob directly (no interceptor transformation)
  if (response.data instanceof Blob) {
    return response.data;
  }
  
  // Fallback: create blob from response data if needed
  return new Blob([response.data], { type: 'application/pdf' });
};
